/**
 * TrackerDashboard - Main dashboard view
 * Shows: Live Ticker, Cycle Progress, Active Operators List
 */

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { TrackerLayout } from "./TrackerLayout";
import { getJson } from "@/lib/http";
import type {
    TrackerSummaryResponse,
    TrackerOperatorSummary,
    TrackerOperatorState
} from "@shared/tracker-types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Eye } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

const POLL_INTERVAL_ACTIVE = 15000;
const POLL_INTERVAL_BACKGROUND = 60000;

export default function TrackerDashboard() {
    const searchParams = new URLSearchParams(window.location.search);
    const cycleId = searchParams.get("cycleId");
    const retailerId = searchParams.get("retailerId");

    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
    useEffect(() => {
        const handleVisibility = () => setIsTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    const pollInterval = isTabVisible ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_BACKGROUND;

    const { data, isLoading, isRefetching, refetch, dataUpdatedAt } = useQuery<TrackerSummaryResponse | null>({
        queryKey: ["tracker-summary", cycleId],
        queryFn: async () => {
            if (!cycleId) return null;
            return await getJson<TrackerSummaryResponse>(`/api/analytics/tracker/summary/${cycleId}`);
        },
        enabled: !!cycleId,
        refetchInterval: pollInterval,
        staleTime: 10000
    });

    const stats = data?.cycleStats;
    const operators = data?.operators || [];
    const hasAssignments = (stats?.totalStores ?? 0) > 0;

    const getStateBadge = (state: TrackerOperatorState) => {
        switch (state) {
            case "ACTIVE":
                return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
            case "IDLE":
                return (
                    <Badge variant="outline" className="text-orange-500 border-orange-200 bg-orange-50">
                        Idle
                    </Badge>
                );
            case "OFFLINE":
                return <Badge variant="outline" className="text-gray-400">Offline</Badge>;
        }
    };

    const getLastSyncLabel = (isoDate: string | null) => {
        if (!isoDate) return "Never";
        try {
            return formatDistanceToNow(new Date(isoDate), { addSuffix: true });
        } catch {
            return "Invalid date";
        }
    };

    const activeCount = operators.filter(o => o.state === "ACTIVE").length;
    const completedToday = operators.reduce((sum, o) => sum + o.completedToday, 0);
    const issuesToday = operators.reduce((sum, o) => sum + o.issuesToday, 0);

    // Build query string preserving both retailerId and cycleId
    const cycleQueryObj = new URLSearchParams();
    if (retailerId) cycleQueryObj.set("retailerId", retailerId);
    if (cycleId) cycleQueryObj.set("cycleId", cycleId);
    const cycleQuery = cycleQueryObj.toString() ? `?${cycleQueryObj.toString()}` : "";

    return (
        <TrackerLayout
            title="Tracker Dashboard"
            lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null}
            onRefresh={() => refetch()}
            isRefreshing={isRefetching}
        >
            {!cycleId ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                    Select a cycle to view tracker data
                </div>
            ) : isLoading && !data ? (
                <div className="flex h-full items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : (
                <div className="space-y-6 max-w-7xl mx-auto">
                    {/* Live Ticker */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-2xl font-bold">{activeCount}</div>
                                <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">
                                    Active Operators
                                </p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-2xl font-bold">
                                    {stats?.completedStores || 0}
                                    <span className="text-sm font-normal text-gray-400">
                                        {" "}/ {stats?.totalStores || 0}
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">
                                    Stores Completed
                                </p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-2xl font-bold">{completedToday}</div>
                                <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">
                                    Completed Today
                                </p>
                            </CardContent>
                        </Card>
                        <Card className={issuesToday > 0 ? "border-orange-200" : ""}>
                            <CardContent className="pt-6">
                                <div className={`text-2xl font-bold ${issuesToday > 0 ? "text-orange-500" : "text-gray-400"}`}>
                                    {issuesToday || "-"}
                                </div>
                                <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mt-1">
                                    Issues Today
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Cycle Progress */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Cycle Progress</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <Progress
                                        value={
                                            stats?.totalStores
                                                ? Math.round((stats.completedStores / stats.totalStores) * 100)
                                                : 0
                                        }
                                        className="flex-1 h-4"
                                    />
                                    <span className="text-lg font-bold text-green-600">
                                        {stats?.totalStores
                                            ? Math.round((stats.completedStores / stats.totalStores) * 100)
                                            : 0}%
                                    </span>
                                </div>

                                {stats?.byRegion && stats.byRegion.length > 1 && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                                        {stats.byRegion.map(region => (
                                            <div key={region.region} className="text-sm border rounded-lg p-3">
                                                <div className="font-medium">{region.region}</div>
                                                <div className="text-muted-foreground text-xs">
                                                    {region.completed}/{region.total} (
                                                    {region.total > 0
                                                        ? Math.round((region.completed / region.total) * 100)
                                                        : 0}%)
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {!hasAssignments && (
                        <Card className="border-dashed">
                            <CardContent className="py-10 text-center text-muted-foreground">
                                No stores are assigned for this cycle yet.
                            </CardContent>
                        </Card>
                    )}

                    {/* Operators List */}
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>Operators</CardTitle>
                            <Link href={`/admin/activation-cockpit/progress/operators${cycleQuery}`}>
                                <Button variant="outline" size="sm">View All</Button>
                            </Link>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[250px]">Operator</TableHead>
                                        <TableHead>State</TableHead>
                                        <TableHead>Current Store</TableHead>
                                        <TableHead className="text-center">Today</TableHead>
                                        <TableHead className="text-right">Last Sync</TableHead>
                                        <TableHead className="w-[100px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {operators.slice(0, 10).map((op: TrackerOperatorSummary) => (
                                        <TableRow
                                            key={op.operatorId}
                                            className={op.state === "ACTIVE" ? "bg-green-50/50" : ""}
                                        >
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-8 w-8">
                                                        <AvatarFallback>{op.operatorName.charAt(0)}</AvatarFallback>
                                                    </Avatar>
                                                    {op.operatorName}
                                                </div>
                                            </TableCell>
                                            <TableCell>{getStateBadge(op.state)}</TableCell>
                                            <TableCell className="text-gray-600">
                                                {op.currentStoreName || <span className="text-gray-300 italic">None</span>}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <span className="text-sm">
                                                    {op.completedToday}
                                                    {op.issuesToday > 0 && (
                                                        <span className="text-orange-500 ml-1">
                                                            ({op.issuesToday} issue{op.issuesToday !== 1 ? "s" : ""})
                                                        </span>
                                                    )}
                                                </span>
                                            </TableCell>
                                            <TableCell
                                                className="text-right text-xs text-gray-500"
                                                title={op.lastSyncAt ? format(new Date(op.lastSyncAt), "PPpp") : "Never"}
                                            >
                                                {getLastSyncLabel(op.lastSyncAt)}
                                            </TableCell>
                                            <TableCell>
                                                <Link href={`/admin/activation-cockpit/operator/${op.operatorId}${cycleQuery}`}>
                                                    <Button variant="ghost" size="sm">
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        Route
                                                    </Button>
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {operators.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                No operators assigned to this cycle.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </div>
            )}
        </TrackerLayout>
    );
}
